import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MapPin } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useI18n } from '@/i18n'
import { enqueueCommand } from '@/offline'
import {
  Badge,
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  ScanField,
  useToast,
} from '@/ui'
import type { PutAwayPayload, ScanRefusal } from '../commands'
import '../commands'
import {
  declarationInfo,
  placePath,
  type BagView,
  type SampleView,
  type StoreState,
} from '../projection'
import { checkPlace, checkPlaceBag, checkPlaceSample, classifyScan, lotCountHere } from '../rules'
import { BagLine, Counter, ScanButton } from './bits'
import { useRefusalText } from './useRefusalText'
import { DeclarationBadge } from './DeclarationBadge'
import { useDraftState, useNow } from '../useStore'

interface PlaceDraft {
  location_id: string | null
  typed_code: string | null
  bags: Array<{ container_id: string; scanned_at: string }>
  samples: Array<{ sample_id: string; scanned_at: string }>
}

const emptyPlace = (): PlaceDraft => ({
  location_id: null,
  typed_code: null,
  bags: [],
  samples: [],
})

/**
 * F5.3 Put away and F5.9 Move share this screen (F5.9-a): scan the place label, then each bag (and,
 * at the sample store, each seal); the phone refuses what the spec says it refuses and shows the
 * lot's count at the new place. One put_away / move_bags command per Confirm.
 */
export function PlaceScanFlow({
  state,
  mode,
  initialLocationId,
}: {
  state: StoreState
  mode: 'PUT_AWAY' | 'MOVE'
  initialLocationId?: string | null
}) {
  const { t } = useI18n()
  const toast = useToast()
  const navigate = useNavigate()
  const actor = useActor()
  const now = useNow()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<PlaceDraft>(
    `store.${mode === 'MOVE' ? 'move' : 'putaway'}:${state.siteId}`,
    mode === 'MOVE' ? 'store.move' : 'store.putaway',
    () => ({ ...emptyPlace(), location_id: initialLocationId ?? null }),
    actor.user_id,
  )
  const [placeError, setPlaceError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const location = draft.location_id ? (state.locations.get(draft.location_id) ?? null) : null
  const listed = useMemo(
    () => draft.bags.map((b) => state.bags.get(b.container_id)).filter((b): b is BagView => !!b),
    [draft.bags, state.bags],
  )
  const listedSamples = draft.samples
    .map((s) => state.samples.get(s.sample_id))
    .filter((s): s is SampleView => !!s)
  const decl = location ? declarationInfo(state, location.id, now) : null

  // Bags waiting on the dock: STORED with their place at the site itself (not yet in a bay).
  const dock = useMemo(
    () =>
      [...state.bags.values()].filter(
        (b) =>
          b.status === 'STORED' &&
          !b.shipment_id &&
          b.location_id &&
          state.locations.get(b.location_id)?.level === 'SITE' &&
          !draft.bags.some((x) => x.container_id === b.container_id),
      ),
    [state, draft.bags],
  )

  const setPlace = (code: string | null, entry?: 'SCAN' | 'TYPED') => {
    setPlaceError(null)
    if (!code) return update((d) => ({ ...d, location_id: null, typed_code: null }))
    const k = classifyScan(state, code)
    if (k.kind !== 'LOCATION') return setPlaceError(refusal('TAG_WRONG_KIND'))
    const problem = checkPlace(state, k.location)
    if (problem) return setPlaceError(refusal(problem))
    update((d) => ({
      ...d,
      location_id: k.location.id,
      typed_code: entry === 'TYPED' ? k.location.code : null,
    }))
  }

  const validateItem = (code: string): string | null => {
    if (!location) return refusal('LEVEL_NOT_ALLOWED')
    const k = classifyScan(state, code)
    let r: ScanRefusal | 'DUPLICATE' | null
    if (k.kind === 'BAG') r = checkPlaceBag(state, k.bag, { mode, location, now, onList: listed })
    else if (k.kind === 'SEAL') r = checkPlaceSample(state, k.sample, location, listedSamples)
    else if (k.kind === 'DEAD') r = k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED'
    else if (k.kind === 'LOCATION') r = 'TAG_WRONG_KIND'
    else r = 'TAG_UNKNOWN'
    return r ? refusal(r) : null
  }
  const addItem = (code: string) => {
    const k = classifyScan(state, code)
    const at = new Date().toISOString()
    if (k.kind === 'BAG')
      update((d) =>
        d.bags.some((b) => b.container_id === k.bag.container_id)
          ? d
          : { ...d, bags: [...d.bags, { container_id: k.bag.container_id, scanned_at: at }] },
      )
    else if (k.kind === 'SEAL')
      update((d) =>
        d.samples.some((s) => s.sample_id === k.sample.sample_id)
          ? d
          : { ...d, samples: [...d.samples, { sample_id: k.sample.sample_id, scanned_at: at }] },
      )
  }

  const confirm = async () => {
    if (!location || (listed.length === 0 && listedSamples.length === 0)) return
    setSaving(true)
    try {
      const payload: PutAwayPayload = {
        location_id: location.id,
        typed_code: draft.typed_code,
        items: [
          ...draft.bags.map((b) => ({
            container_id: b.container_id,
            qr_payload: state.bags.get(b.container_id)!.qr_payload,
            scanned_at: b.scanned_at,
          })),
          ...draft.samples.map((s) => ({
            sample_id: s.sample_id,
            qr_payload: state.samples.get(s.sample_id)!.seal_payload,
            scanned_at: s.scanned_at,
          })),
        ],
      }
      const parents = [
        ...new Set(
          [
            ...listed.map((b) => b.pending_command_id),
            ...listedSamples.map((s) => s.pending_command_id),
          ].filter((x): x is string => !!x),
        ),
      ]
      await enqueueCommand({
        type: mode === 'MOVE' ? 'move_bags' : 'put_away',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      await clear()
      toast.success(
        t(mode === 'MOVE' ? 'store.place.moved' : 'store.place.putAway', {
          count: payload.items.length,
          place: location.code,
        }),
      )
      navigate('/m')
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null
  const lotIds = [...new Set(listed.map((b) => b.unit_id))]

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-3">
          <Field
            label={
              <BilingualLabel k={mode === 'MOVE' ? 'store.place.newLabel' : 'store.place.label'} />
            }
            hint={t('store.place.labelHint')}
            error={placeError ?? undefined}
            required
          >
            <ScanField
              value={location?.code ?? null}
              onChange={(c, meta) => setPlace(c, meta?.entry)}
              disabled={listed.length + listedSamples.length > 0}
            />
          </Field>
          {location && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-[15px]">
                <MapPin className="text-amber-dark" aria-hidden="true" />
                <span>{placePath(state, location.id)}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {decl && <DeclarationBadge info={decl} />}
                {location.role && location.role !== 'STORAGE' && (
                  <Badge tone="accent">{t(`store.role.${location.role}` as const)}</Badge>
                )}
                {draft.typed_code && <Badge>{t('store.place.typed')}</Badge>}
              </div>
              {decl &&
                decl.status !== 'CLEAN' &&
                location.role !== 'CONVENTIONAL' &&
                location.role !== 'SAMPLE_STORE' &&
                location.role !== 'HOLD_AREA' && (
                  <Banner
                    tone="warn"
                    action={
                      <Link
                        className="font-mono text-[12px] font-bold uppercase underline"
                        to={`/m/store/declaration?bay=${encodeURIComponent(location.id)}`}
                      >
                        {t('store.declaration.sign')}
                      </Link>
                    }
                  >
                    {t('store.place.notDeclaredWarn')}
                  </Banner>
                )}
              {decl?.status === 'CLEAN' && (
                <p className="text-[13px] text-muted">{t('store.place.cleanWarn')}</p>
              )}
              {location.role === 'SAMPLE_STORE' && (
                <p className="text-[13px] text-muted">{t('store.place.sampleStoreHint')}</p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {location && (
        <>
          <ScanButton
            label={
              <BilingualLabel
                k={
                  location.role === 'SAMPLE_STORE' ? 'store.place.scanSeal' : 'store.place.scanBag'
                }
              />
            }
            validate={validateItem}
            onScan={addItem}
          />
          <div className="flex gap-2">
            <Counter label={t('store.bags')} value={listed.length} />
            {listedSamples.length > 0 && (
              <Counter label={t('store.samples')} value={listedSamples.length} />
            )}
          </div>
          {lotIds.length > 0 && (
            <Card>
              <CardHeader eyebrow={t('store.place.lotsHere')} />
              <CardBody className="py-2">
                <ul>
                  {lotIds.map((u) => {
                    const c = lotCountHere(state, u, location.id, listed)
                    const lot = state.lots.get(u)
                    return (
                      <li
                        key={u}
                        className="flex items-center justify-between gap-2 border-b border-rule/70 py-2 last:border-b-0"
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-[14px]">{lot?.code}</span>
                          {lot?.stream === 'CONV' && (
                            <Badge tone="danger">{t('store.badge.ordinary')}</Badge>
                          )}
                        </span>
                        <span className={c.here === c.total ? 'font-mono text-ok' : 'font-mono'}>
                          {t('store.nOfMHere', { n: c.here, m: c.total })}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </CardBody>
            </Card>
          )}
          {listed.some((b) => state.lots.get(b.unit_id)?.stream === 'CONV') && (
            <Banner tone="error">{t('store.place.convBanner')}</Banner>
          )}
          {(listed.length > 0 || listedSamples.length > 0) && (
            <Card>
              <CardBody className="py-1">
                <ul>
                  {[...listed].reverse().map((b) => (
                    <BagLine
                      key={b.container_id}
                      state={state}
                      bag={b}
                      now={now}
                      extra={
                        mode === 'MOVE' && b.location_id ? (
                          <div className="text-[12px] text-muted">
                            {t('store.place.from', { place: placePath(state, b.location_id) })}
                          </div>
                        ) : undefined
                      }
                      onRemove={() =>
                        update((d) => ({
                          ...d,
                          bags: d.bags.filter((x) => x.container_id !== b.container_id),
                        }))
                      }
                    />
                  ))}
                  {listedSamples.map((s) => (
                    <li
                      key={s.sample_id}
                      className="flex items-center justify-between border-b border-rule/70 py-2.5 last:border-b-0"
                    >
                      <span className="font-mono">{s.seal_payload}</span>
                      <Badge tone="info">{t('store.samples')}</Badge>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
          <Button
            size="xl"
            variant="accent"
            fullWidth
            disabled={listed.length + listedSamples.length === 0}
            loading={saving}
            onClick={() => void confirm()}
          >
            <BilingualLabel k="store.place.confirm" />
          </Button>
          {mode === 'MOVE' && (
            <Link
              to="/m/store/replace"
              className="text-center font-mono text-[12px] font-bold uppercase tracking-label text-danger underline"
            >
              {t('store.replace.title')}
            </Link>
          )}
        </>
      )}

      {mode === 'PUT_AWAY' && dock.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('store.place.onDock', { count: dock.length })} />
          <CardBody className="py-1">
            <ul>
              {dock.slice(0, 50).map((b) => (
                <BagLine key={b.container_id} state={state} bag={b} now={now} />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
